<template>
  <div class="p-products">
    <div class="totalCount">
      {{ $t('pages.CompaniesProducts.totalCount', { count: numberLocaleString(meta.totalCount) }) }}
    </div>
    <div class="p-products__items">
      <div v-for="product in products"
           :key="'product_' + product.id"
           class="p-products__item"
      >
        <div v-if="!storeUuid && isEditable && product.editable" class="p-products__checkbox">
          <div v-if="product.gmbSyncResult.status !== 'syncing'">
            <input :id="'product_' + product.id"
                   type="checkbox"
                   class="c-checkbox__input"
                   :checked="checkedIds.includes(product.id)"
                   @change="$emit('change-checkbox', product.id)"
            >
            <label
              class="c-checkbox__label"
              :for="'product_' + product.id"
            />
          </div>
        </div>
        <div class="p-products__header">
          <div
            class="p-photos__image"
          >
            <template v-if="!storeUuid">
              <div v-if="product.gmbSyncResult.status === 'syncing'" :style="{ 'background-image': `url(${ product.image })`, 'cursor': 'not-allowed', 'background-size': 'contain' }" />
              <router-link v-else :to="{ name: 'CompaniesProduct', params: { company_uuid: companyUuid, id: product.id }}"
                           :style="{ 'background-image': `url(${ product.image })`, 'cursor': 'pointer', 'background-size': 'contain' }"
              />
            </template>
            <template v-else>
              <div v-if="product.gmbSyncResult.status === 'syncing'" :style="{ 'background-image': `url(${ product.image })`, 'cursor': 'not-allowed', 'background-size': 'contain' }" />
              <router-link v-else :to="{ name: 'CompaniesLocationStoreProduct', params: { company_uuid: companyUuid, store_uuid: storeUuid, id: product.id }}"
                           :style="{ 'background-image': `url(${ product.image })`, 'cursor': 'pointer', 'background-size': 'contain' }"
              />
            </template>
          </div>
        </div>
        <div class="p-products__summary">
          <div class="p-products__name">
            {{ product.title }}
          </div>
          <div class="p-products__category">
            {{ product.categoryName }}
          </div>
          <div class="p-products__description">
            {{ product.description }}
          </div>
          <div v-if="product.price" class="fontBold">
            {{ numberLocaleString(product.price.value) }}円
          </div>
        </div>
        <div class="p-products__content">
          <p class="statusMsg" :class="{ 'statusMsg__failed': product.gmbSyncResult.status === 'failed' }">
            <span v-if="product.gmbSyncResult.status !== 'unset'"
                  :class="`statusMark__${product.gmbSyncResult.status}`"
                  class="statusMark"
            />
            {{ statusMsg(product) }}
          </p>
          <p v-if="!storeUuid">
            {{ $t('pages.CompaniesProducts.storeCount') }}
            <UnUsedBadge v-if="extractStoreCount(product.registeredStoreCount) === 0" />
            <button v-else class="storeCount" @click="showStoreModal(product.productStores)">
              <span>{{ $t('labels.simpleStoresCount', { count: extractStoreCount(product.registeredStoreCount) }) }}</span>
            </button>
          </p>
        </div>
        <div class="p-products__action">
          <template v-if="storeUuid">
            <router-link :to="{ name: 'CompaniesLocationStoreProduct', params: { company_uuid: companyUuid, store_uuid: storeUuid, id: product.id }}" class="actionButton">
              <Icon name="iconShow" />
              {{ $t('pages.CompaniesLocationStoreProduct.showDetail') }}
            </router-link>
            <div
              v-if="isEditable"
              class="actionButton"
              @click="$emit('handle-delete', product.productStores.filter(store=> store.uuid === storeUuid)[0].id)"
            >
              <Icon name="iconCross" />
              {{ $t('pages.CompaniesLocationStoreProduct.delete') }}
            </div>
          </template>
          <template v-else>
            <router-link :to="{ name: 'CompaniesProduct', params: { company_uuid: companyUuid, id: product.id }}" class="actionButton">
              <Icon name="iconShow" />
              {{ $t('pages.CompaniesProducts.showDetail') }}
            </router-link>
            <router-link v-if="isEditable && product.editable" :to="{ name: 'CompaniesProductEdit', params: { company_uuid: companyUuid, id: product.id }}" class="actionButton">
              <Icon name="iconPencil" />
              {{ $t('pages.CompaniesProducts.edit') }}
            </router-link>
            <div
              v-if="isEditable && product.editable"
              class="actionButton"
              @click="$emit('handle-delete', product.id)"
            >
              <Icon name="iconCross" />
              {{ $t('labels.delete') }}
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import UnUsedBadge from '../CompaniesMediaLibraries/UnUsedBadge'
import Icon from '../../components/Icon'
import { toNumberLocaleString } from '../../modules/localeString'

export default {
  name: 'List',
  components: {
    UnUsedBadge,
    Icon,
  },
  props: {
    products: {
      type: Array,
      required: true
    },
    meta: {
      type: Object,
      required: true
    },
    checkedIds: {
      type: Array,
      default: () => []
    },
    storeUuid: {
      type: String,
      default: null
    },
    isEditable: {
      type: Boolean,
      required: true
    }
  },
  emits: ['change-checkbox', 'handle-delete', 'show-store-modal'],
  computed: {
    companyUuid() {
      return this.$route.params.company_uuid
    },
  },
  methods: {
    extractStoreCount(storeCount) {
      return storeCount
    },
    statusMsg(product) {
      if (product.gmbSyncResult.status === 'failed') {
        return this.$t('pages.CompaniesProducts.status.' + product.gmbSyncResult.status, { count: product.gmbSyncResult.errorCount })
      }

      if (product.gmbSyncResult.status === 'store_coordination_cancellation') {
        return this.$t('pages.CompaniesProducts.status.store_coordination_cancellation', { count: product.gmbSyncResult.coordinationCancellationStoreCount })
      }
      return this.$t('pages.CompaniesProducts.status.' + product.gmbSyncResult.status)
    },
    /**
     * 数値の3桁区切り
     * @param  { Number } value
     * @return { Number }
     */
    numberLocaleString(value) {
      return toNumberLocaleString(value)
    },
    showStoreModal(stores) {
      this.$emit('show-store-modal', stores.map(store => store.storeName))
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../../stylesheets/biz_spa/foundation/_var.scss';
.fontBold {
  font-weight: bold;
}
.totalCount {
  font-weight: bold;
  margin-bottom: 10px;
}
.p-products{
  &__item {
    display: flex;
    padding: 20px;
    border: 1px solid #e4e7eb;
    border-radius: 4px;
    margin-bottom: 15px;
  }
  &__checkbox {
    width: 5%;
    margin: auto 0;
  }
  &__header {
    width: 20%;
    margin-right: 15px;
  }
  &__summary {
    width: 45%;
  }
  &__content {
    width: 15%;
    padding-left: 28px;
  }
  &__action {
    margin: auto 0;
    width: 15%;
  }
  &__name {
    margin-top: 10px;
    font-weight: bold;
  }
  &__description {
    margin: 10px 0;
  }
}
.link {
  display: inline-block;
  margin-top: 16px;
  padding: 8px 24px;
  color: #3A3B3F;
  background: #EEEEEE;
  border-radius: 4px;
  text-decoration: none;
}
.actionButton {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  text-decoration: none;
  cursor: pointer;
  svg {
    margin-right: 10px;
  }
}
.statusMark {
  border-radius: 50%;
  display: inline-block;
  width: 8px;
  height: 8px;
  &__failed {
    background: $color-accent-primary;
  }
  &__synced {
    background: $complete-color;
  }
  &__syncing {
    background: #D2D5DB;
  }
  &__deleting {
    background: #D2D5DB;
  }
  &__store_coordination_cancellation{
    background: #D2D5DB;
  }
}
.statusMsg__failed {
  color: $color-accent-primary;
}
.storeCount {
  span {
    color: #E9446A;
    font-weight: bold;
    text-decoration: underline;
  }
}
</style>
